import { useState, useEffect, useRef } from 'react';
import { useApp } from '@/context/AppContext';
import { useAuth } from '@/context/AuthContext';
import { API, xhrJson, authHeader } from '@/lib/api';
import { ProjectStatusBlock } from '@/components/shared/CountdownTimer';
import { VideoPlayer } from '@/components/shared/VideoPlayer';
import { StatusBadge } from '@/components/shared/StatusBadge';
import MediaArchive, { collectProjectFiles } from '@/components/shared/MediaArchive';
import { STATUS_CONFIG } from '@/data/mockData';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  User, Clock, Film, Palette, FileText, Image, Music,
  Download, Send, ChevronDown, CheckCircle2, AlertCircle,
  Sparkles, Upload, MessageSquare, Briefcase, Loader2, PlayCircle, PackageCheck, Truck
} from 'lucide-react';

const GESTOR_STATUSES = ['received', 'in_production', 'delivered', 'revision_requested'];

function FileIcon({ type }) {
  switch (type) {
    case 'image': return <Image className="w-4 h-4" />;
    case 'video': return <Film className="w-4 h-4" />;
    case 'audio': return <Music className="w-4 h-4" />;
    default: return <FileText className="w-4 h-4" />;
  }
}

function InfoCard({ icon: Icon, label, value }) {
  return (
    <div className="border border-gray-100 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className="w-4 h-4 text-gray-400" />
        <span className="text-[11px] text-gray-400 uppercase tracking-wider">{label}</span>
      </div>
      <span className="text-sm text-gray-700">{value}</span>
    </div>
  );
}

export default function ProjectDetail({ project, onSwitchToProduction }) {
  const { getBrand, updateProjectStatus, addMessage, updateProject, setProductionRef } = useApp();
  const { session } = useAuth();
  const brand = project.brandId ? getBrand(project.brandId) : null;
  const [replyText, setReplyText] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [lifecycleBusy, setLifecycleBusy] = useState(null); // 'consume'|'ready'|'deliver'|'rev'|'rev-free'|null
  const [lifecycleMsg, setLifecycleMsg] = useState(null); // {type, msg}
  const [videoSizes, setVideoSizes] = useState([]);
  const [selectedSize, setSelectedSize] = useState(project.project_size || project.projectSize || 'short');
  // Delivery upload state — Phase 8 fix: dropzone was decorative only.
  const [deliveryFile, setDeliveryFile] = useState(null);        // {id, name, size_label, url, storage_path}
  const [uploadProgress, setUploadProgress] = useState(0);       // 0-100
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [deliveryBusy, setDeliveryBusy] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  // Phase 9 — Production AI Brief snapshot for the manager Overview tab.
  const [brief, setBrief] = useState(null);

  useEffect(() => {
    if (!session?.access_token || !project?.id) return;
    let cancelled = false;
    (async () => {
      const { ok, data } = await xhrJson(
        'GET', `${API}/api/briefs/${project.id}`, null, authHeader(session),
      );
      if (!cancelled && ok) setBrief(data.brief || null);
    })();
    return () => { cancelled = true; };
  }, [session?.access_token, project?.id]);

  // Load size grid once
  useEffect(() => {
    if (!session?.access_token) return;
    let cancelled = false;
    (async () => {
      const { ok, data } = await xhrJson(
        'GET', `${API}/api/credits/video-sizes`, null, authHeader(session),
      );
      if (!cancelled && ok) setVideoSizes(data.video_sizes || []);
    })();
    return () => { cancelled = true; };
  }, [session?.access_token]);

  const sizeCfg = videoSizes.find((s) => s.id === selectedSize);
  const creditsConsumed = project.creditsConsumed ?? project.credits_consumed ?? 0;
  const productionStarted = creditsConsumed > 0;
  const revCount = project.revision_count ?? project.revisionCount ?? 0;
  const includedRev = project.included_revisions ?? project.includedRevisions ?? 1;
  const revisionsLeft = Math.max(0, includedRev - revCount);

  const callLifecycle = async (kind, path, body = null) => {
    if (!session?.access_token) {
      setLifecycleMsg({ type: 'err', msg: 'Sign in as a manager to perform this action.' });
      return null;
    }
    setLifecycleBusy(kind);
    setLifecycleMsg(null);
    const { ok, status, data } = await xhrJson(
      'POST',
      `${API}/api/projects/${project.id}/${path}`,
      body,
      authHeader(session),
    );
    setLifecycleBusy(null);
    if (!ok) {
      const msg = typeof data?.detail === 'string'
        ? data.detail
        : (status === 404
            ? "This project doesn't exist in the database yet. Create or sync the project in Supabase to use this button."
            : `Error ${status || ''}`);
      setLifecycleMsg({ type: 'err', msg });
      return null;
    }
    return data;
  };

  const handleConsumeCredits = async () => {
    const data = await callLifecycle('consume', 'consume-credits', { project_size: selectedSize });
    if (!data) return;
    const charged = data.project?.credits_consumed ?? sizeCfg?.credits_required ?? 1;
    updateProject(project.id, {
      creditsConsumed: charged,
      project_size: selectedSize,
      requested_duration_range: data.project?.requested_duration_range,
      deliveryDeadline: data.delivery_due_at,
      status: 'in_production',
    });
    updateProjectStatus(project.id, 'in_production');
    setLifecycleMsg({ type: 'ok', msg: `Production started — ${charged} credit(s) deducted and SLA countdown started.` });
  };

  const handleMarkReady = async () => {
    const data = await callLifecycle('ready', 'mark-ready');
    if (!data) return;
    updateProject(project.id, { status: 'ready', markedReadyAt: new Date().toISOString() });
    updateProjectStatus(project.id, 'ready');
    setLifecycleMsg({ type: 'ok', msg: 'Project marked as ready for delivery.' });
  };

  const handleDeliver = async () => {
    const data = await callLifecycle('deliver', 'deliver', { asset_ids: [], message: 'Final delivery' });
    if (!data) return;
    updateProject(project.id, { status: 'delivered', deliveredAt: new Date().toISOString() });
    updateProjectStatus(project.id, 'delivered');
    setLifecycleMsg({ type: 'ok', msg: 'Delivery registered — the countdown is off.' });
  };

  const handleRequestRevision = async (free = false) => {
    const data = await callLifecycle(free ? 'rev-free' : 'rev', 'request-revision', { free, notes: null });
    if (!data) return;
    updateProject(project.id, {
      status: 'revision_requested',
      revision_count: (revCount + 1),
    });
    updateProjectStatus(project.id, 'revision_requested');
    const tail = data.credits_charged > 0
      ? ` (${data.credits_charged} extra credit(s) charged)`
      : data.free ? ' (free revision — our mistake)' : ' (included)';
    setLifecycleMsg({ type: 'ok', msg: `Revision requested${tail}.` });
  };

  const handleStatusChange = (newStatus) => {
    updateProjectStatus(project.id, newStatus);
  };

  const handleSendToProduction = (file) => {
    setProductionRef({
      fileName: file.name,
      fileType: file.type,
      projectId: project.id,
      projectName: project.name,
    });
    onSwitchToProduction?.();
  };

  const handleReply = () => {
    if (!replyText.trim()) return;
    addMessage(project.id, { type: 'team', content: replyText, sender: 'Team (You)' });
    setReplyText('');
  };

  // Phase 8 fix — real delivery upload pipeline.
  const ACCEPTED_MIME = ['video/mp4', 'video/quicktime']; // MP4 + MOV
  const ACCEPTED_EXT = /\.(mp4|mov)$/i;
  const MAX_BYTES = 500 * 1024 * 1024;

  const handleFilePicked = (f) => {
    if (!f) return;
    setUploadError(null);
    // Validation
    const extOk = ACCEPTED_EXT.test(f.name);
    const mimeOk = !f.type || ACCEPTED_MIME.includes(f.type);
    if (!extOk && !mimeOk) {
      setUploadError('Invalid file type. Use MP4 or MOV.');
      return;
    }
    if (f.size > MAX_BYTES) {
      setUploadError('File exceeds 500MB limit.');
      return;
    }
    // Kick off XHR upload so we can surface a progress bar (fetch can't).
    const form = new FormData();
    form.append('file', f);
    form.append('bucket', 'project-files');
    form.append('source', 'team');

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/api/storage/upload/${project.id}`, true);
    const ah = authHeader(session);
    if (ah?.Authorization) xhr.setRequestHeader('Authorization', ah.Authorization);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      setUploading(false);
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText || '{}');
          setDeliveryFile(data.file || null);
          setUploadProgress(100);
        } catch {
          setUploadError('Upload completed but response was invalid.');
        }
      } else {
        // Parse server error into a plain string (avoid rendering objects).
        let msg = `Upload failed (status ${xhr.status}).`;
        try {
          const body = JSON.parse(xhr.responseText || '{}');
          // eslint-disable-next-line no-console
          console.error('Upload error payload:', body);
          const parsed = toErrorString(body);
          if (parsed && parsed !== 'Upload failed') msg = parsed;
        } catch { /* keep default msg */ }
        setUploadError(msg);
        setUploadProgress(0);
      }
    };
    xhr.onerror = () => { setUploading(false); setUploadError('Network error while uploading.'); setUploadProgress(0); };
    xhr.ontimeout = () => { setUploading(false); setUploadError('Upload timed out.'); setUploadProgress(0); };
    xhr.timeout = 10 * 60 * 1000; // 10 min ceiling for a 500MB file on slow links
    setUploading(true);
    setUploadProgress(0);
    xhr.send(form);
  };

  const handleDropzoneClick = () => {
    if (uploading) return;
    fileInputRef.current?.click();
  };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFilePicked(file);
  };
  const handleResetUpload = () => {
    setDeliveryFile(null);
    setUploadProgress(0);
    setUploadError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Normalize any error shape (string | {detail} | FastAPI 422 list of dicts)
  // into a plain string so React can safely render it. Never passes objects.
  const toErrorString = (err) => {
    if (!err) return 'Upload failed';
    if (typeof err === 'string') return err;
    if (Array.isArray(err)) {
      return err.map((e) => e?.msg || e?.message || 'Upload failed').join('; ');
    }
    if (typeof err === 'object') {
      if (Array.isArray(err.detail)) {
        return err.detail.map((e) => e?.msg || e?.message || 'Upload failed').join('; ');
      }
      return err.message || err.detail || err.msg || 'Upload failed';
    }
    return String(err);
  };

  const handleDeliverFinal = async () => {
    if (!deliveryFile || deliveryBusy) return;
    setDeliveryBusy(true);
    setUploadError(null);
    // Backend expects asset_ids as list of dicts {kind, storage_path, bucket, name?}
    const assetPayload = deliveryFile
      ? [{
          kind: 'video',
          storage_path: deliveryFile.storage_path,
          bucket: 'project-files',
          name: deliveryFile.name,
          id: deliveryFile.id,
          url: deliveryFile.url || null,
        }]
      : [];
    const { ok, data } = await xhrJson(
      'POST',
      `${API}/api/projects/${project.id}/deliver`,
      { asset_ids: assetPayload, message: 'Final delivery' },
      authHeader(session),
    );
    setDeliveryBusy(false);
    if (!ok) {
      // Log full raw error for debugging; only render a string in the UI.
      // eslint-disable-next-line no-console
      console.error('Delivery failed:', data);
      setUploadError(toErrorString(data));
      return;
    }
    // Mirror into local AppContext so the chat + status badge update instantly.
    // Backend is source of truth; this is UI-optimistic only.
    const deliveryId = data?.delivery?.id || null;
    const deliveryFileMeta = {
      id: deliveryFile.id,
      name: deliveryFile.name,
      type: 'video',
      size: deliveryFile.size_label || '',
      url: deliveryFile.url,
      storage_path: deliveryFile.storage_path,
      bucket: 'project-files',
    };
    const deliveryPayload = {
      videoUrl: deliveryFile.url || null,
      thumbnail: null,
      storage_path: deliveryFile.storage_path,
      bucket: 'project-files',
      deliveryId,
      projectId: project.id,
      deliveredAt: new Date().toISOString(),
      files: [deliveryFileMeta],
    };
    updateProject(project.id, { delivery: deliveryPayload });
    updateProjectStatus(project.id, 'delivered');
    addMessage(project.id, {
      type: 'delivery',
      content: "Your video is ready! Here's the final delivery:",
      sender: 'system',
      delivery: deliveryPayload,
    });
  };

  const allFiles = collectProjectFiles(project);

  return (
    <div className="flex flex-col h-full" data-testid="project-detail">
      {/* Header */}
      <div className="px-4 md:px-6 py-4 border-b border-gray-200/60">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="text-base md:text-lg font-medium text-gray-900 font-['Outfit'] truncate" data-testid="gestor-project-name">
              {project.name}
            </h1>
            <StatusBadge status={project.status} />
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {project.status !== 'delivered' && (
              <ProjectStatusBlock status={project.status} deadline={project.deliveryDeadline} />
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="change-status-btn"
                  variant="outline"
                  size="sm"
                  className="bg-white/50 border-gray-200/60 text-gray-600 hover:bg-white/70 hover:text-gray-900 text-xs"
                >
                  Status <ChevronDown className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white/80 backdrop-blur-md border-gray-200/60">
                {GESTOR_STATUSES.map(s => (
                  <DropdownMenuItem
                    key={s}
                    data-testid={`status-option-${s}`}
                    onClick={() => handleStatusChange(s)}
                    className="text-gray-600 hover:bg-gray-50 focus:bg-gray-50 focus:text-gray-900 cursor-pointer text-xs"
                  >
                    {STATUS_CONFIG[s]?.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
        <div className="px-4 md:px-6 border-b border-gray-200">
          <TabsList className="bg-transparent h-auto p-0 gap-0 w-full justify-start">
            {[
              { value: 'overview', label: 'Overview', icon: Briefcase },
              { value: 'chat', label: 'Chat', icon: MessageSquare },
              { value: 'files', label: 'Files', icon: FileText },
              { value: 'delivery', label: 'Delivery', icon: Upload },
            ].map(tab => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                data-testid={`tab-${tab.value}`}
                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-gray-900 data-[state=active]:border-b-2 data-[state=active]:border-gray-900 rounded-none px-4 py-3 text-gray-400 text-sm flex items-center gap-2 transition-colors"
              >
                <tab.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {/* Overview */}
        <TabsContent value="overview" className="flex-1 m-0 overflow-hidden">
          <ScrollArea className="h-full">
            <div className="p-4 md:p-6 space-y-5 max-w-4xl">
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                <InfoCard icon={User} label="Client" value={project.client.name} />
                <InfoCard icon={Palette} label="Brand" value={brand?.name || 'None'} />
                <InfoCard icon={Film} label="Type" value={project.videoType || 'N/A'} />
                <InfoCard icon={Clock} label="Duration" value={project.duration || 'N/A'} />
                <InfoCard icon={Sparkles} label="Style" value={project.style || 'N/A'} />
                <InfoCard icon={Clock} label="Created" value={new Date(project.createdAt).toLocaleDateString()} />
              </div>

              {project.description && (
                <div className="border border-gray-100 rounded-xl p-5">
                  <h3 className="text-xs text-gray-400 uppercase tracking-wider mb-2">Brief Summary</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{project.description}</p>
                </div>
              )}

              {/* Phase 9 — AI Brief from the client production assistant */}
              {brief && (brief.summary_for_manager || brief.brand_name || brief.objective || (brief.uploaded_assets && brief.uploaded_assets.length > 0)) && (
                <div data-testid="manager-brief-card" className="border border-gray-100 rounded-xl p-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs text-gray-400 uppercase tracking-wider">Production brief</h3>
                    <span
                      data-testid="manager-brief-status"
                      className={`text-[10px] uppercase tracking-[0.12em] font-medium px-2 py-0.5 rounded-full ${
                        brief.readiness_status === 'confirmed'
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                          : brief.readiness_status === 'ready'
                            ? 'bg-amber-50 text-amber-700 border border-amber-100'
                            : 'bg-gray-50 text-gray-500 border border-gray-100'
                      }`}
                    >
                      {brief.readiness_status === 'confirmed' ? 'Confirmed'
                        : brief.readiness_status === 'ready' ? 'Ready'
                        : 'Collecting'}
                    </span>
                  </div>

                  {brief.summary_for_manager && (
                    <pre data-testid="manager-brief-summary" className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap font-sans bg-gray-50/60 rounded-lg p-3 border border-gray-100">
                      {brief.summary_for_manager}
                    </pre>
                  )}

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11px]">
                    {brief.brand_name && (
                      <div className="border border-gray-100 rounded-lg px-2.5 py-1.5">
                        <span className="text-gray-400 uppercase tracking-[0.1em] block">Brand</span>
                        <span className="text-gray-700">{brief.brand_name}</span>
                      </div>
                    )}
                    {brief.video_type && (
                      <div className="border border-gray-100 rounded-lg px-2.5 py-1.5">
                        <span className="text-gray-400 uppercase tracking-[0.1em] block">Type</span>
                        <span className="text-gray-700">{brief.video_type}</span>
                      </div>
                    )}
                    {brief.duration_range && (
                      <div className="border border-gray-100 rounded-lg px-2.5 py-1.5">
                        <span className="text-gray-400 uppercase tracking-[0.1em] block">Duration</span>
                        <span className="text-gray-700">{brief.duration_range}</span>
                      </div>
                    )}
                    {brief.audience && (
                      <div className="border border-gray-100 rounded-lg px-2.5 py-1.5 col-span-2 sm:col-span-3">
                        <span className="text-gray-400 uppercase tracking-[0.1em] block">Audience</span>
                        <span className="text-gray-700">{brief.audience}</span>
                      </div>
                    )}
                    {brief.style && (
                      <div className="border border-gray-100 rounded-lg px-2.5 py-1.5">
                        <span className="text-gray-400 uppercase tracking-[0.1em] block">Style</span>
                        <span className="text-gray-700">{brief.style}</span>
                      </div>
                    )}
                    {brief.estimated_credits != null && (
                      <div className="border border-gray-100 rounded-lg px-2.5 py-1.5">
                        <span className="text-gray-400 uppercase tracking-[0.1em] block">Est. credits</span>
                        <span className="text-gray-700 tabular-nums">{brief.estimated_credits}</span>
                      </div>
                    )}
                    {Array.isArray(brief.uploaded_assets) && brief.uploaded_assets.length > 0 && (
                      <div className="border border-gray-100 rounded-lg px-2.5 py-1.5">
                        <span className="text-gray-400 uppercase tracking-[0.1em] block">Assets</span>
                        <span className="text-gray-700">{brief.uploaded_assets.length} file(s)</span>
                      </div>
                    )}
                  </div>

                  {Array.isArray(brief.uploaded_assets) && brief.uploaded_assets.length > 0 && (
                    <div data-testid="manager-brief-assets" className="border-t border-gray-100 pt-3">
                      <span className="text-[10px] uppercase tracking-[0.12em] text-gray-400 block mb-2">Reference assets</span>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {brief.uploaded_assets.map((a, i) => (
                          <a
                            key={i}
                            href={a.url || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`brief-asset-${i}`}
                            className="flex items-center gap-2 px-2.5 py-1.5 text-[11px] text-gray-600 bg-gray-50/60 hover:bg-gray-100 border border-gray-100 rounded-lg transition-colors"
                          >
                            <FileIcon type={a.type} />
                            <div className="min-w-0 flex-1">
                              <div className="truncate">{a.name}</div>
                              {a.role && (
                                <div className="text-[9px] uppercase tracking-[0.1em] text-emerald-600 mt-0.5">
                                  {a.role}{a.priority ? ` · P${a.priority}` : ''}
                                </div>
                              )}
                            </div>
                            <span className="text-gray-400 shrink-0">
                              {a.size ? `${Math.round(a.size / 1024)} KB` : ''}
                            </span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {brief.concept && (
                    <div>
                      <span className="text-[10px] uppercase tracking-[0.12em] text-gray-400 block mb-1">Concept</span>
                      <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap">{brief.concept}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Phase 1 — Lifecycle controls */}
              <div data-testid="lifecycle-card" className="border border-gray-100 rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs text-gray-400 uppercase tracking-wider">Project lifecycle</h3>
                  <div className="flex items-center gap-3 text-[11px] text-gray-400">
                    <span>
                      Credits: <span className="tabular-nums text-gray-700">{creditsConsumed}</span>
                    </span>
                    <span>
                      Revisions: <span className="tabular-nums text-gray-700">{revCount}/{includedRev}</span>
                    </span>
                  </div>
                </div>

                {/* Size picker — only when production hasn't started */}
                {!productionStarted && videoSizes.length > 0 && (
                  <div data-testid="size-picker" className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                    {videoSizes.map((s) => {
                      const active = selectedSize === s.id;
                      const range = s.min_seconds && s.max_seconds
                        ? `${s.min_seconds}–${s.max_seconds}s`
                        : '';
                      return (
                        <button
                          key={s.id}
                          data-testid={`size-${s.id}`}
                          onClick={() => setSelectedSize(s.id)}
                          className={`text-left px-3 py-2 rounded-xl border transition-all ${
                            active
                              ? 'border-gray-900 bg-gray-900/[0.04]'
                              : 'border-gray-200 bg-white/40 hover:border-gray-300'
                          }`}
                        >
                          <span className="text-xs font-medium text-gray-900 block">{s.label || s.name}</span>
                          <span className="text-[10px] text-gray-400 block">{range}</span>
                          <span className="text-[10px] text-emerald-600 tabular-nums">{s.credits_required} cr.</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button
                    data-testid="start-production-btn"
                    onClick={handleConsumeCredits}
                    disabled={lifecycleBusy !== null || productionStarted}
                    className="bg-gray-900 hover:bg-gray-800 text-white text-xs rounded-xl"
                    size="sm"
                  >
                    {lifecycleBusy === 'consume'
                      ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      : <PlayCircle className="w-3.5 h-3.5 mr-1.5" />}
                    {productionStarted
                      ? `Production started (−${creditsConsumed} cr.)`
                      : `Start production (−${sizeCfg?.credits_required ?? 1} cr.)`}
                  </Button>
                  <Button
                    data-testid="mark-ready-btn"
                    onClick={handleMarkReady}
                    disabled={lifecycleBusy !== null}
                    variant="outline"
                    size="sm"
                    className="text-xs rounded-xl border-gray-200 bg-white/60"
                  >
                    {lifecycleBusy === 'ready'
                      ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      : <PackageCheck className="w-3.5 h-3.5 mr-1.5" />}
                    Mark ready
                  </Button>
                  <Button
                    data-testid="deliver-btn"
                    onClick={handleDeliver}
                    disabled={lifecycleBusy !== null}
                    variant="outline"
                    size="sm"
                    className="text-xs rounded-xl border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                  >
                    {lifecycleBusy === 'deliver'
                      ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      : <Truck className="w-3.5 h-3.5 mr-1.5" />}
                    Deliver
                  </Button>
                </div>

                {/* Revisions row — appears once delivered or in revision */}
                {(project.status === 'delivered' || project.status === 'revision_requested' || revCount > 0) && (
                  <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
                    <Button
                      data-testid="request-revision-btn"
                      onClick={() => handleRequestRevision(false)}
                      disabled={lifecycleBusy !== null}
                      variant="outline"
                      size="sm"
                      className="text-xs rounded-xl border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100"
                    >
                      {lifecycleBusy === 'rev'
                        ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        : null}
                      {revisionsLeft > 0
                        ? `Request revision (${revisionsLeft} included)`
                        : 'Extra revision (charges 1 cr.)'}
                    </Button>
                    <Button
                      data-testid="request-revision-free-btn"
                      onClick={() => handleRequestRevision(true)}
                      disabled={lifecycleBusy !== null}
                      variant="outline"
                      size="sm"
                      className="text-xs rounded-xl border-gray-200 bg-white/60 text-gray-600"
                    >
                      {lifecycleBusy === 'rev-free'
                        ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        : null}
                      Free revision (manager)
                    </Button>
                  </div>
                )}

                {lifecycleMsg && (
                  <div
                    data-testid="lifecycle-msg"
                    className={`mt-3 text-xs rounded-lg px-3 py-2 border ${
                      lifecycleMsg.type === 'ok'
                        ? 'text-emerald-700 bg-emerald-50 border-emerald-100'
                        : 'text-rose-700 bg-rose-50 border-rose-100'
                    }`}
                  >
                    {lifecycleMsg.msg}
                  </div>
                )}
                <p className="text-[11px] text-gray-400 mt-2 leading-relaxed">
                  Start production uses the chosen size and charges the credits for that range. When you deliver, the countdown stops. Revisions due to our mistake are free (manager button); new changes charge extra once included revisions are used up.
                </p>
              </div>

              <div className="border border-gray-100 rounded-xl p-5">
                <h3 className="text-xs text-gray-400 uppercase tracking-wider mb-3">Internal Notes</h3>
                {project.internalNotes ? (
                  <p className="text-sm text-gray-500 leading-relaxed whitespace-pre-wrap mb-3">{project.internalNotes}</p>
                ) : (
                  <p className="text-sm text-gray-300 italic mb-3">No notes yet.</p>
                )}
                <div className="flex gap-2">
                  <input
                    data-testid="internal-note-input"
                    value={internalNote}
                    onChange={e => setInternalNote(e.target.value)}
                    placeholder="Add a note..."
                    className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 outline-none focus:border-gray-300 transition-colors"
                  />
                  <Button
                    data-testid="add-note-btn"
                    size="sm"
                    onClick={() => {
                      if (internalNote.trim()) {
                        updateProject(project.id, {
                          internalNotes: (project.internalNotes ? project.internalNotes + '\n' : '') + internalNote
                        });
                        setInternalNote('');
                      }
                    }}
                    className="bg-gray-900 hover:bg-gray-800 text-white text-xs"
                  >Add</Button>
                </div>
              </div>

            </div>
          </ScrollArea>
        </TabsContent>

        {/* Chat */}
        <TabsContent value="chat" className="flex-1 m-0 flex flex-col min-h-0 overflow-hidden">
          <ScrollArea className="flex-1">
            <div className="p-4 md:p-6 space-y-3 max-w-3xl">
              {project.messages.map(msg => (
                <div key={msg.id}>
                  {msg.type === 'status_update' && (
                    <div className="flex justify-center py-1">
                      <span className="text-[11px] text-gray-400">{msg.content}</span>
                    </div>
                  )}
                  {msg.type === 'user' && (
                    <div className="flex justify-end">
                      <div className="bg-gray-100 text-gray-800 rounded-2xl rounded-br-sm p-3 max-w-[80%]">
                        <span className="text-[10px] text-gray-400 block mb-1">{project.client.name}</span>
                        {msg.content && <p className="text-sm leading-relaxed">{msg.content}</p>}
                        {msg.files && (
                          <div className="mt-2 space-y-1">
                            {msg.files.map((f, i) => (
                              <div key={i} className="flex items-center gap-2 text-xs text-gray-500 group/file">
                                <FileIcon type={f.type} /> <span className="truncate">{f.name}</span> <span className="text-gray-400">{f.size}</span>
                                {f.type === 'image' && (
                                  <button
                                    data-testid={`send-to-production-${i}`}
                                    onClick={() => handleSendToProduction(f)}
                                    className="opacity-0 group-hover/file:opacity-100 transition-opacity ml-auto flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100"
                                  >
                                    <Sparkles className="w-3 h-3" />
                                    Production
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {(msg.type === 'system' || msg.type === 'team') && (
                    <div className="flex justify-start">
                      <div className="text-gray-600 py-1 max-w-[80%]">
                        <span className="text-[10px] text-gray-400 block mb-1">
                          {msg.type === 'team' ? msg.sender : 'Ruido.ai'}
                        </span>
                        <p className="text-sm leading-relaxed">{msg.content}</p>
                      </div>
                    </div>
                  )}
                  {msg.type === 'brief' && (
                    <div className="flex justify-start">
                      <div data-testid={`manager-chat-brief-${msg.id}`} className="bg-emerald-50/40 border border-emerald-100 text-gray-700 rounded-2xl p-3 max-w-[90%]">
                        <span className="text-[10px] uppercase tracking-[0.12em] text-emerald-700 font-medium block mb-1">Production brief</span>
                        {msg.content && (
                          <pre className="text-sm leading-relaxed whitespace-pre-wrap font-sans mb-2">{msg.content}</pre>
                        )}
                        {Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {msg.attachments.map((a, i) => (
                              a.type === 'image' && a.url ? (
                                <a key={i} href={a.url} target="_blank" rel="noopener noreferrer" className="block rounded-lg overflow-hidden border border-emerald-100 bg-white">
                                  <div className="aspect-square bg-gray-100">
                                    <img src={a.url} alt={a.name} loading="lazy" className="w-full h-full object-cover" />
                                  </div>
                                  <div className="px-2 py-1 text-[10px] text-gray-500 truncate">
                                    {a.name}{a.role ? ` · ${String(a.role).toUpperCase()}` : ''}
                                  </div>
                                </a>
                              ) : (
                                <a key={i} href={a.url || '#'} target={a.url ? '_blank' : undefined} rel={a.url ? 'noopener noreferrer' : undefined} className="flex items-center gap-2 px-2 py-1.5 text-xs text-gray-500 bg-white/60 border border-emerald-100 rounded-lg">
                                  <FileIcon type={a.type} />
                                  <span className="truncate flex-1">{a.name}</span>
                                </a>
                              )
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {msg.type === 'delivery' && (
                    <div className="flex justify-start">
                      <div className="bg-gray-50 border border-gray-200 text-gray-600 rounded-2xl p-3 max-w-[90%]">
                        <span className="text-[10px] text-gray-400 block mb-1">Delivery</span>
                        <p className="text-sm mb-2">{msg.content}</p>
                        {(msg.delivery?.videoUrl || msg.delivery?.files?.[0]?.url) && (
                          <VideoPlayer
                            videoUrl={msg.delivery.videoUrl || msg.delivery.files?.[0]?.url}
                            thumbnail={msg.delivery.thumbnail}
                            fileName={msg.delivery.files?.[0]?.name}
                            fileSize={msg.delivery.files?.[0]?.size}
                            storagePath={msg.delivery.storage_path || msg.delivery.files?.[0]?.storage_path}
                            bucket={msg.delivery.bucket || msg.delivery.files?.[0]?.bucket || 'project-files'}
                            projectId={msg.delivery.projectId || project.id}
                            deliveryId={msg.delivery.deliveryId}
                            fileIndex={0}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
          <div className="p-4 border-t border-gray-200">
            <div className="flex gap-2 max-w-3xl">
              <input
                data-testid="gestor-reply-input"
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) handleReply(); }}
                placeholder="Reply to client..."
                className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 outline-none focus:border-gray-300 transition-colors"
              />
              <Button
                data-testid="gestor-reply-btn"
                onClick={handleReply}
                disabled={!replyText.trim()}
                className="bg-gray-900 hover:bg-gray-800 text-white rounded-xl disabled:opacity-30"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Files */}
        <TabsContent value="files" className="flex-1 m-0 overflow-hidden">
          <ScrollArea className="h-full">
            <div className="p-4 md:p-6 max-w-4xl">
              <MediaArchive files={allFiles} maxVisible={12} showProjectLabel={false} />
            </div>
          </ScrollArea>
        </TabsContent>

        {/* Delivery */}
        <TabsContent value="delivery" className="flex-1 m-0 overflow-hidden">
          <ScrollArea className="h-full">
            <div className="p-4 md:p-6 max-w-4xl">
              {project.status === 'delivered' && project.delivery ? (
                <div className="border border-gray-200 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <CheckCircle2 className="w-5 h-5 text-gray-400" />
                    <h3 className="text-base font-medium text-gray-700 font-['Outfit']">Delivered</h3>
                    <span className="text-xs text-gray-400 ml-auto">{new Date(project.delivery.deliveredAt).toLocaleString()}</span>
                  </div>
                  {(project.delivery.videoUrl || project.delivery.files?.[0]?.url) && (
                    <VideoPlayer
                      videoUrl={project.delivery.videoUrl || project.delivery.files?.[0]?.url}
                      thumbnail={project.delivery.thumbnail}
                      fileName={project.delivery.files?.[0]?.name}
                      fileSize={project.delivery.files?.[0]?.size}
                      storagePath={project.delivery.storage_path || project.delivery.files?.[0]?.storage_path}
                      bucket={project.delivery.bucket || project.delivery.files?.[0]?.bucket || 'project-files'}
                      projectId={project.delivery.projectId || project.id}
                      deliveryId={project.delivery.deliveryId}
                      fileIndex={0}
                    />
                  )}
                  {project.delivery.files && (
                    <div className="mt-4 space-y-2">
                      {project.delivery.files.map((f, i) => (
                        <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg text-sm">
                          <FileIcon type={f.type} />
                          <span className="text-gray-700 truncate">{f.name}</span>
                          <span className="text-gray-400 ml-auto shrink-0">{f.size}</span>
                          {f.url ? (
                            <a
                              href={f.url}
                              download={f.name}
                              rel="noopener noreferrer"
                              className="text-gray-400 hover:text-gray-600 transition-colors"
                              data-testid={`delivery-file-download-${i}`}
                            >
                              <Download className="w-4 h-4" />
                            </a>
                          ) : (
                            <Download className="w-4 h-4 text-gray-300" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="border border-gray-200 rounded-xl p-6">
                  <h3 className="text-base font-medium text-gray-900 font-['Outfit'] mb-4">Upload Final Delivery</h3>

                  {/* Hidden input wired to the dropzone. Accepts MP4 / MOV. */}
                  <input
                    ref={fileInputRef}
                    data-testid="delivery-file-input"
                    type="file"
                    accept="video/mp4,video/quicktime,.mp4,.mov"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleFilePicked(f);
                    }}
                  />

                  <div
                    data-testid="delivery-upload-zone"
                    role="button"
                    tabIndex={0}
                    onClick={handleDropzoneClick}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleDropzoneClick(); }
                    }}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                      uploading
                        ? 'border-gray-200 bg-gray-50 cursor-wait'
                        : dragActive
                          ? 'border-emerald-300 bg-emerald-50/40 cursor-pointer'
                          : 'border-gray-200 hover:border-gray-300 bg-gray-50 cursor-pointer'
                    }`}
                  >
                    {deliveryFile && !uploading ? (
                      <div className="flex flex-col items-center gap-2" data-testid="delivery-uploaded">
                        <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                        <p className="text-sm text-gray-700 font-medium truncate max-w-full px-4">{deliveryFile.name}</p>
                        <p className="text-[11px] text-gray-400">
                          {deliveryFile.size_label || 'Uploaded'} · Ready to deliver
                        </p>
                        <button
                          type="button"
                          data-testid="delivery-upload-replace"
                          onClick={(e) => { e.stopPropagation(); handleResetUpload(); }}
                          className="text-[11px] text-gray-500 underline underline-offset-2 hover:text-gray-700 mt-1"
                        >
                          Choose a different file
                        </button>
                      </div>
                    ) : uploading ? (
                      <div className="flex flex-col items-center gap-3" data-testid="delivery-uploading">
                        <Loader2 className="w-7 h-7 text-gray-400 animate-spin" />
                        <p className="text-sm text-gray-600">Uploading… {uploadProgress}%</p>
                        <div className="w-full max-w-xs h-1.5 rounded-full bg-gray-200 overflow-hidden">
                          <div
                            className="h-full bg-gray-900 transition-all"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                        <p className="text-sm text-gray-500 mb-1">Drop files here or click to upload</p>
                        <p className="text-[11px] text-gray-400">MP4, MOV up to 500MB</p>
                      </>
                    )}
                  </div>

                  {uploadError && (
                    <p data-testid="delivery-upload-error" className="mt-3 text-xs text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">
                      {uploadError}
                    </p>
                  )}

                  <Button
                    data-testid="deliver-final-btn"
                    onClick={handleDeliverFinal}
                    disabled={!deliveryFile || deliveryBusy || uploading}
                    className="w-full mt-4 bg-gray-900 hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium rounded-xl h-11 transition-all active:scale-[0.98]"
                  >
                    {deliveryBusy ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Delivering…</>
                    ) : (
                      <><CheckCircle2 className="w-4 h-4 mr-2" />Mark as Delivered</>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>
    </div>
  );
}
